import {
  AssessmentLeaderboardItem,
  AssessmentShowFeedback,
} from "../../axios/quiz";

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

// Prefer top-level fields, then nested `config` from the assessment socket payload.
export const getAssessmentConfigValue = (
  data: unknown,
  key: string,
): unknown => {
  const raw = asRecord(data);
  if (!raw) {
    return undefined;
  }

  if (raw[key] !== undefined) {
    return raw[key];
  }

  const config = asRecord(raw.config);
  return config ? config[key] : undefined;
};

export const resolveAssessmentGamificationEnabled = (
  data: unknown,
  fallback = false,
): boolean => {
  const enabled = getAssessmentConfigValue(data, "gamification_enabled");
  if (enabled === true) {
    return true;
  }
  if (enabled === false) {
    return false;
  }

  const competitionMode = getAssessmentConfigValue(data, "competition_mode");
  if (competitionMode === true) {
    return true;
  }

  const raw = asRecord(data);
  if (!raw) {
    return fallback;
  }

  const gamification =
    asRecord(raw.gamification) ??
    asRecord(asRecord(raw.config)?.gamification);
  if (gamification) {
    if (
      gamification.competition_mode === true ||
      gamification.enabled === true ||
      gamification.gamification_enabled === true
    ) {
      return true;
    }
  }

  return fallback;
};

export const resolveAssessmentAnswerEditingEnabled = (
  data: unknown,
  fallback = false,
): boolean => {
  const value = getAssessmentConfigValue(data, "answer_editing_enabled");
  if (typeof value === "boolean") {
    return value;
  }
  return fallback;
};

export const resolveAssessmentShowFeedback = (
  data: unknown,
  fallback?: AssessmentShowFeedback,
): AssessmentShowFeedback | undefined => {
  const value = getAssessmentConfigValue(data, "show_feedback");
  if (
    value === "not_show" ||
    value === "after_answer" ||
    value === "after_completion"
  ) {
    return value;
  }
  return fallback;
};

const rankingKey = (item: AssessmentLeaderboardItem) =>
  `${item.user_score ?? item.score}:${item.mandatory_score ?? 0}`;

const getUserScore = (item: AssessmentLeaderboardItem): number =>
  typeof item.user_score === "number" ? item.user_score : item.score;

const getMandatoryScore = (item: AssessmentLeaderboardItem): number =>
  typeof item.mandatory_score === "number" ? item.mandatory_score : 0;

const getBonusQuestionScore = (item: AssessmentLeaderboardItem): number =>
  typeof item.bonus_question_score === "number"
    ? item.bonus_question_score
    : 0;

const getGamificationBonusScore = (item: AssessmentLeaderboardItem): number =>
  typeof item.gamification_bonus_score === "number"
    ? item.gamification_bonus_score
    : typeof item.bonus_score === "number"
      ? item.bonus_score
      : 0;

export const sortAssessmentLeaderboard = (
  items: AssessmentLeaderboardItem[],
  _currentUserId: string | null,
) =>
  [...items].sort((a, b) => {
    const aUserScore = getUserScore(a);
    const bUserScore = getUserScore(b);
    if (bUserScore !== aUserScore) {
      return bUserScore - aUserScore;
    }

    const aMandatoryScore = getMandatoryScore(a);
    const bMandatoryScore = getMandatoryScore(b);
    if (bMandatoryScore !== aMandatoryScore) {
      return bMandatoryScore - aMandatoryScore;
    }

    return a.name.localeCompare(b.name);
  });

export const getCurrentUserLeaderboardScore = (
  items: AssessmentLeaderboardItem[],
  currentUserId: string | null,
): number => {
  if (!currentUserId) {
    return 0;
  }

  const currentUserItem = items.find((item) => item.user_id === currentUserId);
  return currentUserItem ? getUserScore(currentUserItem) : 0;
};

export const getCurrentUserLeaderboardMandatoryScore = (
  items: AssessmentLeaderboardItem[],
  currentUserId: string | null,
): number => {
  if (!currentUserId) {
    return 0;
  }

  const currentUserItem = items.find((item) => item.user_id === currentUserId);
  return currentUserItem ? getMandatoryScore(currentUserItem) : 0;
};

export const getCurrentUserLeaderboardBonusQuestionScore = (
  items: AssessmentLeaderboardItem[],
  currentUserId: string | null,
): number => {
  if (!currentUserId) {
    return 0;
  }

  const currentUserItem = items.find((item) => item.user_id === currentUserId);
  return currentUserItem ? getBonusQuestionScore(currentUserItem) : 0;
};

export const getCurrentUserLeaderboardBonusScore = (
  items: AssessmentLeaderboardItem[],
  currentUserId: string | null,
): number => {
  if (!currentUserId) {
    return 0;
  }

  const currentUserItem = items.find((item) => item.user_id === currentUserId);
  return currentUserItem ? getGamificationBonusScore(currentUserItem) : 0;
};

export const getAssessmentUserPlace = (
  items: AssessmentLeaderboardItem[],
  currentUserId: string | null,
  fallbackPlace: number | null = null,
) => {
  if (!currentUserId || items.length === 0) {
    return fallbackPlace;
  }

  const sortedItems = sortAssessmentLeaderboard(items, currentUserId);
  let previousRankingKey: string | null = null;
  let previousRank = 0;

  for (let index = 0; index < sortedItems.length; index += 1) {
    const item = sortedItems[index];
    const itemRankingKey = rankingKey(item);
    const rank =
      previousRankingKey === itemRankingKey ? previousRank : index + 1;

    if (item.user_id === currentUserId) {
      return rank;
    }

    previousRankingKey = itemRankingKey;
    previousRank = rank;
  }

  return fallbackPlace;
};

type DraftFinalizedLookup = {
  finalized?: boolean;
};

const isDraftFinalized = (
  drafts: Record<number, DraftFinalizedLookup | undefined>,
  index: number,
  treatAsFinalized?: ReadonlySet<number>,
) =>
  treatAsFinalized?.has(index) === true || drafts[index]?.finalized === true;

// Next question that still needs Submit & Next (skips already locked answers).
export const findNextUnsubmittedQuestionIndex = (
  totalQuestions: number,
  drafts: Record<number, DraftFinalizedLookup | undefined>,
  fromIndex: number,
  treatAsFinalized?: ReadonlySet<number>,
): number | null => {
  if (totalQuestions <= 0) {
    return null;
  }

  for (let i = fromIndex + 1; i < totalQuestions; i += 1) {
    if (!isDraftFinalized(drafts, i, treatAsFinalized)) {
      return i;
    }
  }
  for (let i = 0; i < fromIndex; i += 1) {
    if (!isDraftFinalized(drafts, i, treatAsFinalized)) {
      return i;
    }
  }
  return null;
};

// Previous question that still needs Submit & Next (no wrap).
export const findPreviousUnsubmittedQuestionIndex = (
  totalQuestions: number,
  drafts: Record<number, DraftFinalizedLookup | undefined>,
  fromIndex: number,
  treatAsFinalized?: ReadonlySet<number>,
): number | null => {
  for (let i = fromIndex - 1; i >= 0; i -= 1) {
    if (i >= totalQuestions) {
      continue;
    }
    if (!isDraftFinalized(drafts, i, treatAsFinalized)) {
      return i;
    }
  }
  return null;
};
